// Helpers for the hub network usage view. Days and months are UTC, matching
// the per-UTC-day buckets the backend keeps.

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

// The backend refuses daily buckets beyond this many days.
export const MAX_DAILY_DAYS = 1100;
// Above this many days a daily chart has no room left, so presets switch to months.
export const AUTO_MONTHLY_ABOVE_DAYS = 92;

export const formatBytes = bytes => {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
    return '–';
  }
  if (bytes >= GB) {
    return `${(bytes / GB).toFixed(2)} GB`;
  }
  if (bytes >= MB) {
    return `${(bytes / MB).toFixed(2)} MB`;
  }
  if (bytes >= KB) {
    return `${(bytes / KB).toFixed(1)} KB`;
  }
  return `${Math.round(bytes)} B`;
};

// Short form for axis ticks: 0, 512 KB, 1.5 MB
export const formatBytesCompact = bytes => {
  if (bytes >= GB) {
    return `${+(bytes / GB).toFixed(1)} GB`;
  }
  if (bytes >= MB) {
    return `${+(bytes / MB).toFixed(1)} MB`;
  }
  if (bytes >= KB) {
    return `${Math.round(bytes / KB)} KB`;
  }
  return `${Math.round(bytes)} B`;
};

export const formatRate = bytesPerMinute => {
  if (bytesPerMinute === null || bytesPerMinute === undefined) {
    return '–';
  }
  if (bytesPerMinute >= MB) {
    return `${(bytesPerMinute / MB).toFixed(2)} MB/min`;
  }
  return `${(bytesPerMinute / KB).toFixed(2)} KB/min`;
};

export const formatShare = (part, total) =>
  total > 0 ? `${Math.round((part / total) * 100)}%` : '–';

// Splits raw counters into the three figures the view shows. Others is all
// modem traffic minus NTRIP, never below zero.
export const splitUsage = counters => {
  const modemRx = counters?.modem_rx_bytes || 0;
  const modemTx = counters?.modem_tx_bytes || 0;
  const ntripRx = counters?.ntrip_rx_bytes || 0;
  const ntripTx = counters?.ntrip_tx_bytes || 0;
  return {
    total: { rx: modemRx, tx: modemTx, sum: modemRx + modemTx },
    ntrip: { rx: ntripRx, tx: ntripTx, sum: ntripRx + ntripTx },
    others: {
      rx: Math.max(0, modemRx - ntripRx),
      tx: Math.max(0, modemTx - ntripTx),
      sum: Math.max(0, modemRx + modemTx - ntripRx - ntripTx),
    },
  };
};

export const toUtcDateString = date => date.toISOString().slice(0, 10);

const utcDate = (year, month, day) => new Date(Date.UTC(year, month, day));

export const utcToday = () => {
  const now = new Date();
  return utcDate(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
};

export const addUtcDays = (date, days) =>
  utcDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days);

export const daysInRange = (startDate, endDate) =>
  Math.round((Date.parse(endDate) - Date.parse(startDate)) / 86400000) + 1;

export const autoGranularity = days => (days > AUTO_MONTHLY_ABOVE_DAYS ? 'month' : 'day');

// Period presets. Each returns the inclusive UTC dates; the bucket size is
// chosen from the length unless the preset fixes it. firstDay is the first
// day the hub ever reported, used by "All time".
export const PERIOD_PRESETS = [
  {
    id: 'today',
    label: 'Today',
    range: today => ({ start: today, end: today }),
  },
  {
    id: 'thisMonth',
    label: 'This month',
    range: today => ({
      start: utcDate(today.getUTCFullYear(), today.getUTCMonth(), 1),
      end: today,
    }),
  },
  {
    id: 'lastMonth',
    label: 'Last month',
    range: today => ({
      start: utcDate(today.getUTCFullYear(), today.getUTCMonth() - 1, 1),
      end: utcDate(today.getUTCFullYear(), today.getUTCMonth(), 0),
    }),
  },
  {
    id: 'last7',
    label: 'Last 7 days',
    range: today => ({ start: addUtcDays(today, -6), end: today }),
  },
  {
    id: 'last30',
    label: 'Last 30 days',
    range: today => ({ start: addUtcDays(today, -29), end: today }),
  },
  {
    id: 'last12Months',
    label: 'Last 12 months',
    range: today => ({
      start: utcDate(today.getUTCFullYear(), today.getUTCMonth() - 11, 1),
      end: today,
      granularity: 'month',
    }),
  },
  {
    id: 'allTime',
    label: 'All time',
    range: (today, firstDay) => ({
      start: firstDay ? new Date(Date.parse(firstDay)) : today,
      end: today,
    }),
  },
];

export const presetRange = (presetId, firstDay) => {
  const preset = PERIOD_PRESETS.find(item => item.id === presetId);
  const { start, end, granularity } = preset.range(utcToday(), firstDay);
  const startDate = toUtcDateString(start);
  const endDate = toUtcDateString(end);
  return {
    startDate,
    endDate,
    granularity: granularity || autoGranularity(daysInRange(startDate, endDate)),
  };
};

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// "2026-10-28" -> "Oct 28", "2026-10" -> "Oct 2026"
export const formatPeriod = (period, granularity) => {
  const [year, month, day] = period.split('-');
  const monthName = MONTH_NAMES[Number(month) - 1];
  return granularity === 'month' ? `${monthName} ${year}` : `${monthName} ${Number(day)}`;
};

// "2026-10-28" -> "Oct 28, 2026"
export const formatDateLong = dateString => {
  const [year, month, day] = dateString.split('-');
  return `${MONTH_NAMES[Number(month) - 1]} ${Number(day)}, ${year}`;
};

// "Oct 1 – Oct 28, 2026", "Nov 1, 2025 – Oct 28, 2026" or a single day
export const formatDateRange = (startDate, endDate) => {
  if (startDate === endDate) {
    return formatDateLong(startDate);
  }
  if (startDate.slice(0, 4) === endDate.slice(0, 4)) {
    return `${formatPeriod(startDate, 'day')} – ${formatDateLong(endDate)}`;
  }
  return `${formatDateLong(startDate)} – ${formatDateLong(endDate)}`;
};

// Axis maximum: the smallest 1/2/5 x 10^n of the value's unit (B, KB, MB, GB)
// above the value, so ticks read as clean numbers such as 0, 250 KB, 500 KB.
export const niceCeiling = bytes => {
  if (bytes <= 0) {
    return KB;
  }
  const unit = [GB, MB, KB, 1].find(candidate => bytes >= candidate) || 1;
  const value = bytes / unit;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const factor = [1, 2, 5, 10].find(candidate => candidate * magnitude >= value);
  return factor * magnitude * unit;
};
